import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  previewDeploymentRemoval,
  type ForeignIntuneRelationship,
  type RelatedIntuneApp,
  type RemovalPreviewItem,
} from '../services/api'
import { Spinner } from './ui'
import { errorText } from '../utils/errorText'

type RemovalPreview = Awaited<ReturnType<typeof previewDeploymentRemoval>>

const ROLE_LABEL: Record<RelatedIntuneApp['role'], string> = {
  update_only: 'Update-only app',
  older_update_only: 'Older update-only app',
  older_version: 'Older version',
}

/** What removing these deployments deletes from Intune, read while the confirmation is open.
 *
 *  `preview` is the answer to confirm against, and is undefined while it is loading, being
 *  re-read or failed — a dialog enables Remove only when it is set. It is the answer for
 *  `keepUpdateOnly`, chosen from the one response that covers both settings. The query key
 *  carries the ids only while the dialog is open, so a closed dialog holds no answer and reopening it asks
 *  again instead of offering what was true the last time. */
export function useRemovalPreview(ids: string[], open: boolean, keepUpdateOnly = false) {
  // One request answers both settings of the update-only checkbox, so toggling it picks the
  // other answer instead of checking Intune again.
  const query = useQuery({
    queryKey: ['removal-preview', ...(open ? ids : [])],
    queryFn: () => previewDeploymentRemoval(ids),
    enabled: open && ids.length > 0,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
  const data = query.isSuccess && !query.isFetching ? query.data : undefined
  const preview = data && keepUpdateOnly ? (data.keep_update_only ?? data) : data
  return { query, preview }
}

/** Whether removing an install app also removes its update-only app. On by default, as the
 *  removal has always done; off leaves the update-only app as a deployment of its own, still
 *  patching the devices that have the app. Shown only where there is an update-only app. */
export function KeepUpdateOnlyOption({ shown, keep, onChange }: {
  shown: boolean
  keep: boolean
  onChange: (keep: boolean) => void
}) {
  if (!shown) return null
  return (
    <label className="my-2 flex items-start gap-2 text-sm text-theme-text-secondary">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={!keep}
        onChange={(e) => onChange(!e.target.checked)}
      />
      <span>
        Also remove the update-only app. Clear this to keep it: it stays in Intune as a
        deployment of its own and keeps updating the devices that already have the app.
      </span>
    </label>
  )
}

/** What a Remove of a deployment with both apps removes. */
export type RemoveScope = 'deployment' | 'install' | 'update'

/** The app id DELETE /deployments/{id} is sent for, and whether the update-only app is kept:
 *  the whole deployment and the install app alone are removed through the install app, the
 *  update-only app alone through its own id. */
export function removalTarget(
  scope: RemoveScope,
  primaryId: string,
  updateOnlyId: string | null | undefined,
): { id: string; keepUpdateOnly: boolean } {
  if (scope === 'update' && updateOnlyId) return { id: updateOnlyId, keepUpdateOnly: false }
  return { id: primaryId, keepUpdateOnly: scope === 'install' && !!updateOnlyId }
}

/** Asks what to remove from a deployment that has an install app and an update-only app. */
export function RemoveScopeChoice({ shown, scope, onChange, disabled }: {
  shown: boolean
  scope: RemoveScope
  onChange: (scope: RemoveScope) => void
  disabled?: boolean
}) {
  if (!shown) return null
  const options: [RemoveScope, string, string][] = [
    ['deployment', 'The whole deployment', 'The install app and the update-only app.'],
    ['install', 'Only the install app', 'The update-only app stays and keeps updating the devices that have the app.'],
    ['update', 'Only the update-only app', 'The install app and its assignments are not changed.'],
  ]
  return (
    <fieldset className="my-3 space-y-2" aria-label="What to remove">
      {options.map(([value, label, hint]) => (
        <label key={value} className="flex items-start gap-2 text-sm text-theme-text-secondary">
          <input
            type="radio"
            name="remove-scope"
            className="mt-1"
            checked={scope === value}
            disabled={disabled}
            onChange={() => onChange(value)}
          />
          <span>
            <span className="font-medium text-theme-text-primary">{label}</span>
            <span className="block text-xs text-theme-text-tertiary">{hint}</span>
          </span>
        </label>
      ))}
    </fieldset>
  )
}

/** "also deletes its update-only app and 2 older versions" — for a row that has no room for a list. */
export function relatedSummary(related: RelatedIntuneApp[]): string | null {
  const updateOnly = related.filter((r) => r.role !== 'older_version').length
  const older = related.length - updateOnly
  const parts = [
    updateOnly === 1 ? 'its update-only app' : updateOnly > 1 ? `${updateOnly} update-only apps` : '',
    older === 1 ? '1 older version' : older > 1 ? `${older} older versions` : '',
  ].filter(Boolean)
  return parts.length ? `also deletes ${parts.join(' and ')}` : null
}

/** The Entra groups these removals leave behind: every name the previews list, and whether any
 *  preview could not say (no publish record names them). */
export function groupsLeft(items: RemovalPreviewItem[]): { names: string[]; unknown: boolean } {
  return {
    names: [...new Set(items.flatMap((i) => i.groups ?? []))],
    unknown: items.some((i) => i.groups == null),
  }
}

/** A removal deletes app objects and assignments, never groups. Publish matches a generated
 *  group by name and reuses one it finds, so a group can serve other deployments too; the only
 *  safe instruction is the conditional one. */
export function GroupsNote({ items }: { items: RemovalPreviewItem[] }) {
  const { names, unknown } = groupsLeft(items)
  if (!names.length && !unknown) return null
  return (
    <p className="mt-2 text-xs text-theme-text-tertiary">
      {names.length
        ? `Security groups created by publishing stay in Entra; delete them by hand if nothing else uses them: ${names.join(', ')}.`
        : 'Any security groups created by publishing stay in Entra; delete them by hand if nothing else uses them.'}
      {names.length > 0 && unknown && (
        <> Some of these deployments have no publish record, so their groups are not listed.</>
      )}
    </p>
  )
}

/** The other Intune apps deleted along with one deployment. */
export function RelatedAppList({ related }: { related: RelatedIntuneApp[] }) {
  if (related.length === 0) return null
  return (
    <ul className="my-3 max-h-48 overflow-y-auto rounded border border-theme-border-primary divide-y divide-theme-border-primary">
      {related.map((r) => (
        <li key={r.intune_app_id} className="px-3 py-2 text-sm text-theme-text-primary">
          {ROLE_LABEL[r.role]}
          <span className="text-theme-text-tertiary">
            {r.display_name ? ` · ${r.display_name}` : ''}
            {r.version ? ` · ${r.version}` : ''}
            {r.assignment_count != null
              ? ` · ${r.assignment_count} assignment${r.assignment_count === 1 ? '' : 's'}`
              : ''}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** True when Intune will refuse the removal of any of these deployments: another app depends
 *  on one of the apps it would delete. */
export function removalBlocked(items: RemovalPreviewItem[] | undefined): boolean {
  return (items ?? []).some((d) => (d.blocked_by ?? []).length > 0)
}

const relationshipName = (r: ForeignIntuneRelationship) => r.display_name || r.intune_app_id

/** What the removal does about the relationships other Intune apps hold over the apps it
 *  deletes: a supersedence is taken off the other app; a dependency stops the removal, since
 *  taking it off would change what that app installs. */
export function RelationshipNotes({ item }: { item: RemovalPreviewItem }) {
  const blocked = item.blocked_by ?? []
  const unlinked = item.relationships ?? []
  if (blocked.length === 0 && unlinked.length === 0) return null
  return (
    <div className="my-3 space-y-1 text-sm">
      {blocked.length > 0 && (
        <p className="text-red-700 dark:text-red-400">
          Cannot be removed: {blocked.map(relationshipName).join(', ')}{' '}
          {blocked.length === 1 ? 'depends' : 'depend'} on it. Remove that dependency in Intune
          first.
        </p>
      )}
      {unlinked.length > 0 && (
        <p className="text-theme-text-secondary">
          {unlinked.map(relationshipName).join(', ')}{' '}
          {unlinked.length === 1 ? 'supersedes' : 'supersede'} it; that supersedence is removed.
        </p>
      )}
    </div>
  )
}

/** Loading, failure, and the case where Intune could not supply names and assignment counts. */
export function RemovalPreviewStatus({ query }: { query: UseQueryResult<RemovalPreview> }) {
  if (query.isFetching) {
    return (
      <p className="flex items-center gap-2 text-sm text-theme-text-secondary">
        <Spinner className="h-4 w-4" /> Checking what will be deleted from Intune…
      </p>
    )
  }
  if (query.error) {
    return (
      <p className="text-sm text-red-700 dark:text-red-400">
        {errorText(query.error, 'Could not check what will be deleted from Intune.')} Close this
        dialog and try again.
      </p>
    )
  }
  if (query.data && !query.data.intune_readable) {
    return (
      <p className="text-sm text-theme-text-tertiary">
        Intune could not be read, so app names and assignment counts are not shown.
      </p>
    )
  }
  return null
}
